<script setup>
import { storeToRefs } from 'pinia';
import { ref } from 'vue';

import { api } from '@/api/client.js';
import AnalyticsChart from '@/components/charts/AnalyticsChart.vue';
import PanelCard from '@/components/panels/PanelCard.vue';
import { usePanelState } from '@/composables/usePanelState.js';
import { usePoll } from '@/composables/usePoll.js';
import { useTenantStore } from '@/stores/tenant.js';
import { CUSTOM_EVENTS_EMPTY } from '@/utils/emptyStateCopy.js';

// Top eventos de Tracker.track(name) con sparkline de las últimas
// 24 h por nombre. Fila simple (estilo HotspotRow sin drill-down).

const tenant = useTenantStore();
const { activeAppId, timeWindow } = storeToRefs(tenant);
const ps = usePanelState('loading');
const rows = ref([]);

async function fetchFn({ signal }) {
  if (!activeAppId.value) return;
  return api.events.customEvents({ appId: activeAppId.value, window: timeWindow.value, signal });
}

const { tick, status: refreshStatus, lastUpdatedAt } = usePoll(fetchFn, {
  watch: [() => tenant.changeKey],
  onSuccess(result) {
    if (!result || result.ok !== true) return;
    rows.value = result.rows ?? [];
    ps.setState(rows.value.length === 0 ? 'empty' : 'data');
  },
  onError(err) {
    ps.setState('error', {
      message: "Couldn't load custom events",
      detail: err?.message ?? '',
    });
  },
});

ps.setRetryHandler(() => tick());

// Exponer ps para los panelRefs del LandingView (error global / retry all).
defineExpose({ ps, refreshStatus });

function fmtCount(n) {
  return (n ?? 0).toLocaleString('en-US');
}
</script>

<template>
  <PanelCard
    title="Custom events"
    help-key="customEvents"
    :state="ps.state.value"
    :empty-headline="CUSTOM_EVENTS_EMPTY.headline"
    :empty-body="CUSTOM_EVENTS_EMPTY.body"
    empty-action="larger-window"
    empty-integration-guide
    :error-message="ps.errorMessage.value"
    :error-detail="ps.errorDetail.value"
    :on-retry="ps.retry"
    :refresh-status="refreshStatus"
    :last-updated-at="lastUpdatedAt"
  >
    <ul class="custom-events">
      <li
        v-for="row in rows"
        :key="row.name"
        class="custom-events__row"
      >
        <span
          class="custom-events__name"
          :title="row.name"
        >{{ row.name }}</span>
        <AnalyticsChart
          type="line"
          compact
          :labels="row.sparkline.map((p) => p.ts)"
          :datasets="[{ label: row.name, data: row.sparkline.map((p) => p.value) }]"
          :aria-label="`${row.name} trend in selected window`"
          class="custom-events__spark"
        />
        <span class="custom-events__count">{{ fmtCount(row.count) }}</span>
      </li>
    </ul>
    <template
      v-if="rows.length"
      #tableFallback
    >
      <table class="ce-table">
        <caption class="visually-hidden">
          Custom events by count in this window
        </caption>
        <thead>
          <tr>
            <th scope="col">
              Event name
            </th>
            <th
              scope="col"
              class="numeric"
            >
              Count
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="`t-${row.name}`"
          >
            <td>{{ row.name }}</td>
            <td class="numeric">
              {{ row.count }}
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </PanelCard>
</template>

<style scoped>
.custom-events {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.custom-events__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(6rem, 10rem) auto;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-lg);
  border: var(--bw-thin) solid var(--border-default);
}

.custom-events__row:hover {
  background: var(--bg-subtle);
}

.custom-events__name {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
  font-family: var(--font-mono);
  font-size: var(--text-xs);
  color: var(--fg-primary);
}

.custom-events__spark {
  color: var(--chart-1);
  flex-shrink: 0;
}

.custom-events__count {
  font-feature-settings: "tnum";
  font-weight: var(--fw-medium);
  min-width: 3rem;
  text-align: right;
}

.ce-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-xs);
}

.ce-table th,
.ce-table td {
  padding: var(--space-1) var(--space-2);
  text-align: left;
  border-bottom: var(--bw-thin) solid var(--border-default);
}

.ce-table th {
  color: var(--fg-secondary);
  font-weight: var(--fw-medium);
}

.ce-table .numeric {
  text-align: right;
  font-feature-settings: "tnum";
}
</style>
